import { useNavigate } from "@solidjs/router"
import { createMemo, createSignal } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Form } from "@/designSystem/components/form"
import { Switch } from "@/designSystem/components/switch"
import { TextField } from "@/designSystem/components/textField"
import { responsivePaddingClass } from "@/designSystem/utility/responsivePadding.css"
import { defaultJsonContent } from "@/lib/defaultJsonContent"
import { createBlogRouter } from "@/routes/posts-router"
import { createStoriesRouter } from "@/routes/stories-router"

type Props = {
  url?: string | null
  onSubmit?: (props: { id: string; url: string; title: string }) => void
}
export function AddStoryForm(props: Props) {
  const navigate = useNavigate()
  const { replicache, user } = useAppContext()

  const [storyUrl, setStoryUrl] = createSignal(props.url || undefined)
  const dirty = createMemo(() => !!storyUrl()?.length)
  const { paths: storiesPaths } = createStoriesRouter()
  const { paths: blogPaths } = createBlogRouter()

  async function onSubmit() {
    const url = storyUrl()
    if (!url) return

    const storyId = ulid()
    const bookmarkId = ulid()
    const title = "Untitled"

    await replicache.mutate.bookmarkStory({
      bookmark_id: bookmarkId,
      story_id: storyId,
      user_id: user.id,
      url,
      metadated_at: null,
      dc_title: title,
      dc_description: null,
      logo_url: null,
      image_url: null,
      dc_created_at: null,
      feed_url: null,
      dc_identifier: url || null,
    })

    if (blogmark()) {
      const postId = ulid()
      await replicache.mutate.createPost({
        id: postId,
        bookmark_id: bookmarkId,
        title,
        json: defaultJsonContent,
      })
      navigate(blogPaths({ filter: "draft" }).post({ id: postId, title }).url)
      return
    }

    try {
      browser.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID, {
        addedBookmark: url,
      })
    } catch (_error) {
    } finally {
      navigate(storiesPaths().story({ id: storyId, dc_title: title }).url)
    }
  }
  const [blogmark, setBlogmark] = createSignal(false)

  return (
    <Box class={responsivePaddingClass}>
      <Form
        title="add stories"
        description="copy and paste the address of the story you want to add. You can find it on top of your browser."
        hue="emerald"
        iconName="book-marked"
        onSubmit={onSubmit}
      >
        <TextField value={storyUrl()} onChange={setStoryUrl}>
          <TextField.Label>story address (url)</TextField.Label>
          <TextField.Input type="text" placeholder="https://" />
        </TextField>
        <Switch
          label="blogmark"
          description="write a blog post about this story."
          checked={blogmark()}
          onChange={() => setBlogmark(!blogmark())}
          hue="emerald"
        />
        <Form.Save hue="emerald" iconName="book-marked" disabled={!dirty()} />
      </Form>
    </Box>
  )
}
